import { StepHelpers } from "@shared/components/Stepper";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  ChevronLeft,
  FileText,
  Users,
  Calendar,
  Clock,
  MessageSquare,
  AlertCircle,
  ShieldCheck,
  AlertTriangle,
  Send,
  Loader2,
  DollarSign,
  CreditCard as PaymentIcon,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, PaginationInfo } from "@shared/types/table";
import { format } from "date-fns";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useForm } from "react-hook-form";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { usePaymentMethod } from "@features/cx/payments/hooks";
import {
  useTimeSlotChoices,
  useUtilityRequestConfiguration,
} from "@features/cx/reconnect/hooks";

interface ReviewSubmitStepProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep: (stepName: string) => void;
  stopTracking: () => void;
  stepHelpers?: StepHelpers;
  onPrevious?: () => void;
  onNext?: (data?: any) => void;
  isSubmitting?: boolean;
  error?: any;
}

export const ReviewSubmitStep: React.FC<ReviewSubmitStepProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stopTracking,
  stepHelpers,
  onPrevious,
  onNext,
  isSubmitting = false,
  error,
}) => {
  // Retrieve data from previous steps
  const selectionData = useMemo(
    () => stepHelpers?.getStepData(0) || {},
    [stepHelpers],
  );
  const requestDetails = useMemo(
    () => stepHelpers?.getStepData(1) || {},
    [stepHelpers],
  );

  const { data: paymentMethodsData } = usePaymentMethod({
    remoteUtilityId,
  });

  const { data: timeSlotChoicesData } = useTimeSlotChoices();
  const { data: utilityConfigData } = useUtilityRequestConfiguration({
    remoteUtilityId,
    requestType: "Reconnection",
    disablePagination: true,
  });

  // Resolve labels if missing
  const reasonLabel = useMemo(() => {
    if (requestDetails.reasonLabel) return requestDetails.reasonLabel;
    if (!requestDetails.reason || !utilityConfigData) return "N/A";
    const reason = utilityConfigData.result?.find(
      (r) => r.id.toString() === requestDetails.reason,
    );
    return reason?.name || requestDetails.reason;
  }, [requestDetails.reason, requestDetails.reasonLabel, utilityConfigData]);

  const timeSlotLabel = useMemo(() => {
    if (requestDetails.timeSlotLabel) return requestDetails.timeSlotLabel;
    if (!requestDetails.timeSlot || !timeSlotChoicesData)
      return "Not specified";
    const slot = timeSlotChoicesData.result?.find(
      (s) => s.key.toString() === requestDetails.timeSlot,
    );
    return slot?.value || requestDetails.timeSlot;
  }, [
    requestDetails.timeSlot,
    requestDetails.timeSlotLabel,
    timeSlotChoicesData,
  ]);

  const selectedConsumers = selectionData.selectedConsumers || [];
  const consumerCount = selectedConsumers.length;
  const currency = useCurrency();

  // Fee Calculation Constants
  // Fee Calculation from Step 2 Data
  const RECONNECTION_FEE = Number(requestDetails.reconnectionFee) || 0;
  const PER_ACCOUNT_TOTAL = RECONNECTION_FEE;
  const GRAND_TOTAL = PER_ACCOUNT_TOTAL * consumerCount;

  // Local state for search and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  // Local filtering logic
  const filteredConsumers = useMemo(() => {
    if (!searchTerm.trim()) return selectedConsumers;
    const term = searchTerm.toLowerCase();
    return selectedConsumers.filter((consumer: any) => {
      const name =
        `${consumer.firstName || ""} ${consumer.lastName || ""}`.toLowerCase();
      const nameAlt =
        `${consumer.first_name || ""} ${consumer.last_name || ""}`.toLowerCase();
      const consumerNo = (consumer.consumerNo || "").toLowerCase();
      return (
        name.includes(term) ||
        nameAlt.includes(term) ||
        consumerNo.includes(term)
      );
    });
  }, [selectedConsumers, searchTerm]);

  // Local pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredConsumers.slice(startIndex, startIndex + pageSize);
  }, [filteredConsumers, currentPage, pageSize]);

  const paginationInfo: PaginationInfo = {
    currentPage,
    totalPages: Math.ceil(filteredConsumers.length / pageSize),
    totalItems: filteredConsumers.length,
    itemsPerPage: pageSize,
    startItem: (currentPage - 1) * pageSize + 1,
    endItem: Math.min(currentPage * pageSize, filteredConsumers.length),
  };

  const columns: TableColumn<any>[] = [
    {
      header: "ACCOUNT ID",
      key: "consumerNo",
      render: (value, row) => value || row?.accountId || "N/A",
    },
    {
      header: "NAME",
      key: "firstName",
      render: (value, row) => {
        const first = value || row?.name || row?.first_name || "";
        const last = row?.lastName || row?.last_name || "";
        return `${first} ${last}`.trim() || "N/A";
      },
    },
  ];

  const paymentForm = useForm({
    defaultValues: {
      payment_method: "",
      amount: GRAND_TOTAL,
      cashReceived: "",
      paymentDate: "",
      chequeNo: "",
      bankName: "",
      accountHolderName: "",
      routingNumber: "",
      bankAccountNumber: "",
      chequeDate: "",
      chequeAmount: GRAND_TOTAL,
      onlineAmount: GRAND_TOTAL,
      referenceNumber: "",
      onlineDate: "",
    },
  });

  const watchAmount = paymentForm.watch("amount");
  const watchCashReceived = paymentForm.watch("cashReceived");
  const watchPaymentMethod = paymentForm.watch("payment_method");

  const totalDue = Number(watchAmount) || GRAND_TOTAL;
  const received = Number(watchCashReceived) || 0;
  const outstanding = Math.max(0, totalDue - received);

  const activePaymentMethods = useMemo(
    () => paymentMethodsData?.result?.filter((m: any) => m.isActive) || [],
    [paymentMethodsData],
  );

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      if (stepHelpers) {
        const currentData = stepHelpers.getStepData(2) || {};
        stepHelpers.setStepData(2, {
          ...currentData,
          [fieldName]: value,
        });
      }
    },
    [stepHelpers],
  );

  // Set default payment method to Cash when data loads
  useEffect(() => {
    const currentMethod = paymentForm.getValues("payment_method");
    if (!currentMethod && activePaymentMethods.length > 0) {
      const cashMethod = activePaymentMethods.find((m: any) =>
        m.name.toLowerCase().includes("cash"),
      );
      if (cashMethod) {
        paymentForm.setValue("payment_method", cashMethod.code);
        handleFieldChange("payment_method", cashMethod.code);
      }
    }
  }, [activePaymentMethods, paymentForm, handleFieldChange]);

  useEffect(() => {
    // Sync all amount fields with GRAND_TOTAL when it changes
    const currentValues = paymentForm.getValues();
    if (currentValues.amount !== GRAND_TOTAL) {
      paymentForm.setValue("amount", GRAND_TOTAL);
      handleFieldChange("amount", GRAND_TOTAL);
    }
    if (currentValues.onlineAmount !== GRAND_TOTAL) {
      paymentForm.setValue("onlineAmount", GRAND_TOTAL);
      handleFieldChange("onlineAmount", GRAND_TOTAL);
    }
    if (currentValues.chequeAmount !== GRAND_TOTAL) {
      paymentForm.setValue("chequeAmount", GRAND_TOTAL);
      handleFieldChange("chequeAmount", GRAND_TOTAL);
    }
  }, [GRAND_TOTAL, paymentForm, handleFieldChange]);

  const paymentFields: FormField[] = useMemo(
    () => [
      {
        name: "payment_method",
        label: "Payment Method",
        type: "select",
        options:
          activePaymentMethods.map((m: any) => ({
            label: m.name,
            value: m.code,
          })) || [],
        className: "col-span-3",
      },
      // Cash Fields
      {
        name: "amount",
        label: "Amount",
        type: "number",
        disabled: true,
        showWhen: (values) => values.payment_method?.startsWith("Cash"),
        className: "col-span-1",
      },
      {
        name: "cashReceived",
        label: "Cash Received",
        type: "number",
        placeholder: "Enter amount received in cash",
        showWhen: (values) => values.payment_method?.startsWith("Cash"),
        className: "col-span-1",
      },
      {
        name: "paymentDate",
        label: "Date",
        type: "datepicker",
        showWhen: (values) => values.payment_method?.startsWith("Cash"),
        className: "col-span-1",
      },
      // Online Fields
      {
        name: "onlineAmount",
        label: "Amount",
        type: "number",
        disabled: true,
        showWhen: (values) => values.payment_method?.startsWith("Online"),
        className: "col-span-1",
      },
      {
        name: "referenceNumber",
        label: "Reference Number",
        type: "text",
        placeholder: "Enter transaction reference number",
        showWhen: (values) => values.payment_method?.startsWith("Online"),
        className: "col-span-1",
      },
      {
        name: "onlineDate",
        label: "Date",
        type: "datepicker",
        placeholder: "Enter transaction date",
        showWhen: (values) => values.payment_method?.startsWith("Online"),
        className: "col-span-1",
      },
      // Cheque Fields
      {
        name: "chequeNo",
        label: "Cheque Number",
        type: "number",
        placeholder: "Enter cheque number",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
      {
        name: "bankName",
        label: "Bank Name",
        type: "text",
        placeholder: "Enter bank name",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
      {
        name: "accountHolderName",
        label: "Account Holder's Name",
        type: "text",
        placeholder: "Enter account holder's name",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
      {
        name: "routingNumber",
        label: "Routing Number",
        type: "number",
        placeholder: "Enter routing number",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
      {
        name: "bankAccountNumber",
        label: "Bank Account Number",
        type: "number",
        placeholder: "Enter bank account number",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
      {
        name: "chequeDate",
        label: "Cheque Date",
        type: "datepicker",
        placeholder: "Enter cheque date",
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
      {
        name: "chequeAmount",
        label: "Cheque Amount",
        type: "number",
        placeholder: "Enter cheque amount",
        disabled: true,
        showWhen: (values) => values.payment_method?.startsWith("Cheque"),
        className: "col-span-1",
      },
    ],
    [activePaymentMethods],
  );

  const handleFinalSubmit = () => {
    logTimeUntilStep("Review & Submit");
    const data = paymentForm.getValues();
    if (stepHelpers) {
      stepHelpers.setStepData(2, data);
    }
    onNext?.(data);
  };

  const InfoItem = ({
    icon: Icon,
    label,
    value,
    valueColor = "text-neutral-900",
  }: {
    icon: any;
    label: string;
    value: string;
    valueColor?: string;
  }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg border border-neutral-100 bg-neutral-50/50">
      <div className="mt-1 p-1.5 bg-white rounded-md border border-neutral-100 shadow-sm">
        <Icon className="h-4 w-4 text-blue-600" />
      </div>
      <div>
        <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-0.5">
          {label}
        </p>
        <p className={`text-sm font-bold ${valueColor}`}>{value}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 bg-white border rounded-2xl shadow-sm border-neutral-200">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600 rounded-2xl shadow-lg shadow-blue-200">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-neutral-900 tracking-tight">
              Review & Submit
            </h2>
            <p className="text-neutral-500 font-medium">
              Verify your bulk reconnection request details and process payment
            </p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-800 shadow-sm animate-in shake duration-500">
          <div className="p-1.5 bg-white rounded-lg border border-red-100 shadow-sm flex-shrink-0">
            <AlertTriangle size={18} className="text-red-600" />
          </div>
          <div>
            <h4 className="text-sm font-bold uppercase tracking-tight mb-1">
              Submission Failed
            </h4>
            <p className="text-xs font-medium opacity-90">
              {error.message ||
                "An unexpected error occurred while creating the bulk batch. Please try again or contact support."}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-8">
        {/* Left Column: Selection Summary & Fees */}
        <div className="col-span-12 lg:col-span-8 space-y-8">
          {/* Selected Consumers Table */}
          <div className="p-6 border rounded-2xl bg-white shadow-sm border-neutral-200">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Users className="h-6 w-6 text-blue-600" />
                <h4 className="text-xl font-black text-neutral-900 tracking-tight">
                  Selected Consumers
                </h4>
                <Badge
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 font-bold border-blue-100 ml-2"
                >
                  {consumerCount}
                </Badge>
              </div>
            </div>

            <div className="border border-neutral-100 rounded-xl overflow-hidden bg-white shadow-sm p-2">
              <GenericTable
                data={paginatedData}
                columns={columns}
                rowKey="id"
                getRowClassName={() => "text-xs h-12 border-b last:border-b-0"}
                onSearch={setSearchTerm}
                searchPlaceholder="Search consumers..."
                emptyMessage={
                  searchTerm
                    ? `No consumers found matching "${searchTerm}"`
                    : "No consumers selected. Please go back to step 1."
                }
                pagination={paginationInfo}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

          {/* Reconnection Fees Summary Section */}
          <div className="bg-white border rounded-xl shadow-sm border-neutral-200 overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center gap-2 bg-white">
              <DollarSign className="h-5 w-5 text-orange-500" />
              <h4 className="text-lg font-bold text-neutral-900">
                Reconnection Fees Summary
              </h4>
            </div>

            <div className="w-full">
              <table className="w-full text-left">
                <thead className="bg-[#f8f9fc] border-b">
                  <tr>
                    <th className="px-6 py-3 text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                      Fee Description
                    </th>
                    <th className="px-6 py-3 text-[10px] font-bold text-neutral-400 uppercase tracking-widest text-right">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y text-[13px]">
                  {requestDetails.feesBreakdown &&
                  requestDetails.feesBreakdown.length > 0 ? (
                    requestDetails.feesBreakdown.map(
                      (fee: any, idx: number) => (
                        <tr
                          key={idx}
                          className="hover:bg-neutral-50/50 transition-colors"
                        >
                          <td className="px-6 py-4 text-neutral-600 font-medium capitalize">
                            {fee.feeType || "Reconnection Fee"}
                          </td>
                          <td className="px-6 py-4 text-right font-bold text-neutral-800">
                            {currency} {(Number(fee.amount) || 0).toFixed(2)}{" "}
                            <span className="text-neutral-400 font-normal ml-1">
                              × {consumerCount}
                            </span>
                          </td>
                        </tr>
                      ),
                    )
                  ) : (
                    <tr className="hover:bg-neutral-50/50 transition-colors">
                      <td className="px-6 py-4 text-neutral-600 font-medium">
                        Reconnection Fee
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-neutral-800">
                        {currency} {RECONNECTION_FEE.toFixed(2)}{" "}
                        <span className="text-neutral-400 font-normal ml-1">
                          × {consumerCount}
                        </span>
                      </td>
                    </tr>
                  )}
                  <tr className="border-t bg-neutral-50/30">
                    <td className="px-6 py-3 text-[11px] font-bold text-blue-600 uppercase tracking-wider">
                      Per Account Fee
                    </td>
                    <td className="px-6 py-3 text-right font-black text-neutral-800 font-mono text-sm">
                      {currency} {PER_ACCOUNT_TOTAL.toFixed(2)}
                    </td>
                  </tr>
                  <tr className="bg-blue-50/50 border-t-2 border-blue-100">
                    <td className="px-6 py-5 text-sm font-bold text-neutral-800">
                      Total Reconnection Fees ({consumerCount} accounts)
                    </td>
                    <td className="px-6 py-5 text-right font-black text-blue-700 text-xl font-mono">
                      {currency} {GRAND_TOTAL.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right Column: Request Details Summary */}
        <div className="col-span-12 lg:col-span-4 space-y-6">
          <div className="p-5 border rounded-xl bg-white shadow-sm border-neutral-200">
            <div className="flex items-center gap-3 mb-6 px-1">
              <div className="p-2 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-neutral-900 tracking-tight">
                Request Details
              </h3>
            </div>

            <div className="space-y-4">
              <InfoItem icon={AlertCircle} label="Reason" value={reasonLabel} />
              <InfoItem
                icon={Calendar}
                label="Scheduled Date"
                value={
                  requestDetails.scheduledDate &&
                  !isNaN(new Date(requestDetails.scheduledDate).getTime())
                    ? format(
                        new Date(requestDetails.scheduledDate),
                        "MMM dd, yyyy",
                      )
                    : "Not specified"
                }
              />
              <InfoItem icon={Clock} label="Time Slot" value={timeSlotLabel} />
              <InfoItem
                icon={DollarSign}
                label="Total Reconnection Fees"
                value={`${currency} ${GRAND_TOTAL.toFixed(2)}`}
                valueColor="text-blue-700"
              />
              <div className="p-4 rounded-xl border border-neutral-100 bg-neutral-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="h-4 w-4 text-blue-500" />
                  <p className="text-xs font-bold text-neutral-500 uppercase tracking-widest">
                    Additional Notes
                  </p>
                </div>
                <p className="text-sm text-neutral-700 italic leading-relaxed">
                  {requestDetails.consumerRemark ||
                    "No additional comments provided."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Options Section - Full Width */}
        <div className="col-span-12 space-y-6">
          <div className="animate-in fade-in slide-in-from-top-4 duration-300 space-y-6">
            <div className="p-6 bg-white border border-neutral-200 rounded-xl shadow-sm">
              <h3 className="text-sm font-bold text-neutral-800 mb-3 uppercase tracking-wider">
                Payment Details
              </h3>
              <DynamicForm
                form={paymentForm}
                fields={paymentFields}
                gridColumns={3}
                onFieldChange={(name, value) => {
                  handleFieldChange(name, value);
                }}
                config={{
                  spacing: "medium",
                  classes: FIELD_CLASSES,
                  showSubmitButton: false,
                }}
              />

              <div className="mt-6 pt-4 border-t border-neutral-100">
                <div className="rounded-xl border border-blue-100 bg-blue-50/30 overflow-hidden divide-y divide-blue-100">
                  {/* Total Due / Amount row */}
                  <div className="flex justify-between items-center px-4 py-3 text-xs">
                    <span className="text-neutral-500 font-semibold">
                      {watchPaymentMethod?.startsWith("Cash")
                        ? "Total Due:"
                        : "Amount:"}
                    </span>
                    <span className="font-bold text-neutral-800 text-sm">
                      {currency} {GRAND_TOTAL.toFixed(2)}
                    </span>
                  </div>

                  {watchPaymentMethod?.startsWith("Cash") && (
                    <>
                      {/* Cash Received row */}
                      <div className="flex justify-between items-center px-4 py-3 text-xs">
                        <span className="text-neutral-500 font-semibold">
                          Cash Received:
                        </span>
                        <span className="font-bold text-blue-600 text-sm">
                          {currency} {received.toFixed(2)}
                        </span>
                      </div>

                      {/* Remaining Due — shown when cash received is less than total */}
                      {received > 0 && received < totalDue && (
                        <div className="flex justify-between items-center px-4 py-3 text-xs bg-red-50/50">
                          <span className="text-red-600 font-bold">
                            Remaining Due:
                          </span>
                          <span className="font-bold text-red-600 text-sm">
                            {currency} {(totalDue - received).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Change / Return — shown when cash received exceeds total */}
                      {received > totalDue && (
                        <div className="flex justify-between items-center px-4 py-3 text-xs bg-emerald-50/50">
                          <span className="text-emerald-600 font-bold">
                            Change / Return:
                          </span>
                          <span className="font-bold text-emerald-600 text-sm">
                            {currency} {(received - totalDue).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Ready to Submit Banner - Full Width */}
      <div className="p-4 bg-blue-50/30 border border-blue-100 rounded-xl shadow-sm">
        <div className="flex gap-3">
          <ShieldCheck className="h-6 w-6 text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-bold text-blue-900">Ready to Submit</h4>
            <p className="text-xs text-blue-700/80 mt-1 font-medium leading-relaxed">
              Please review all information carefully. Once submitted, the
              reconnection process will be initiated for all{" "}
              <span className="font-bold underline">{consumerCount}</span>{" "}
              selected accounts.
            </p>
          </div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="flex items-center justify-between pt-8 border-t border-neutral-200 mt-8">
        <Button
          onClick={onPrevious}
          variant="ghost"
          disabled={isSubmitting}
          className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 font-bold uppercase tracking-wider text-xs"
        >
          <ChevronLeft size={16} />
          <span>Back to Details</span>
        </Button>

        <Button
          onClick={handleFinalSubmit}
          disabled={isSubmitting || consumerCount < 2}
          className="bg-blue-600 hover:bg-blue-700 text-white px-12 h-12 font-bold shadow-lg shadow-blue-200 flex items-center gap-3 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          {isSubmitting ? (
            <Loader2 className="animate-spin h-5 w-5" />
          ) : (
            <Send size={18} />
          )}
          <span>
            {isSubmitting ? "Submitting Request..." : "Submit Bulk Batch"}
          </span>
        </Button>
      </div>
    </div>
  );
};
